<template>
  <q-page class="q-pa-md radio-page">
    <sem-permissao v-if="permissionDenied" />

    <div v-else class="radio-page__content">
      <div class="text-h6 q-mb-md">Rádio da Sorte</div>

      <div v-if="loading" class="text-body2 text-grey-7">
        Carregando dados da rádio...
      </div>

      <div v-else-if="loadError" class="column q-gutter-sm">
        <div class="text-body2 text-negative">
          {{ loadError }}
        </div>
        <q-btn
          data-test="radio-retry"
          color="primary"
          label="Tentar novamente"
          no-caps
          @click="fetchRadio"
        />
      </div>

      <div v-else-if="envelope" class="column q-gutter-sm">
        <div class="radio-page__header">
          <div class="radio-page__title-line">
            <q-badge
              data-test="radio-status"
              :color="statusInfo.color"
            >
              {{ statusInfo.label }}
            </q-badge>
            <q-badge
              v-if="isLive"
              data-test="radio-live"
              color="positive"
            >
              Ao vivo
            </q-badge>
            <span
              v-else
              data-test="radio-inactive"
              class="radio-page__activity text-grey-7"
            >
              Sem transmissão ativa
            </span>
          </div>
        </div>

        <q-banner
          v-if="statusMessage"
          data-test="radio-status-message"
          class="radio-page__banner"
          :class="'text-' + statusInfo.color"
        >
          {{ statusMessage }}
        </q-banner>

        <q-select
          v-if="showStationSelector"
          data-test="radio-station-select"
          class="radio-page__station"
          :model-value="selectedShortcode"
          :options="stationOptions"
          emit-value
          map-options
          outlined
          dense
          label="Estação"
          :disable="loading || switchingStation"
          :loading="switchingStation"
          @update:model-value="selectStation"
        />
        <div
          v-if="lowerPlansHint"
          data-test="radio-lower-plans-hint"
          class="text-caption text-grey-7"
        >
          {{ lowerPlansHint }}
        </div>

        <q-input
          v-if="streamUrl"
          data-test="radio-stream-url"
          :model-value="streamUrl"
          readonly
          type="textarea"
          autogrow
          class="radio-page__url"
        >
          <template v-slot:append>
            <q-btn
              data-test="radio-copy"
              color="primary"
              label="Copiar"
              no-caps
              :disable="!streamUrl"
              aria-label="Copiar link"
              @click="copyStreamUrl"
            />
            <q-btn
              data-test="radio-open"
              color="primary"
              outline
              label="Abrir"
              no-caps
              :disable="!streamUrl"
              aria-label="Abrir link em nova aba"
              @click="openStreamUrl"
            />
          </template>
        </q-input>

        <div class="radio-page__actions">
          <q-btn
            v-if="canRegenerate"
            data-test="radio-regenerate"
            color="primary"
            :label="streamUrl ? 'Regenerar link' : 'Gerar link'"
            no-caps
            :loading="regenerating"
            :disable="regenerating"
            @click="confirmRegenerate"
          />
          <span
            v-if="expiresAtFormatted"
            data-test="radio-expires-at"
            class="text-body2 text-grey-7"
          >
            Link válido até {{ expiresAtFormatted }}
          </span>
          <span
            v-if="regenerateError"
            data-test="radio-regenerate-error"
            class="text-body2 text-negative"
          >
            {{ regenerateError }}
          </span>
          <span
            v-if="regenerateWarning"
            data-test="radio-regenerate-warning"
            class="text-body2 text-warning"
          >
            {{ regenerateWarning }}
          </span>
        </div>

        <div
          v-if="nowPlayingText || streamUrl"
          class="radio-page__playback"
        >
          <div
            v-if="nowPlayingText"
            data-test="radio-now-playing"
            class="radio-page__now-playing"
          >
            <div class="radio-page__label text-grey-7">
              Tocando agora
            </div>
            <div class="text-body2 text-grey-9">
              {{ nowPlayingText }}
            </div>
          </div>

          <div
            v-if="streamUrl"
            class="radio-page__audio"
          >
            <q-btn
              data-test="radio-play-toggle"
              :color="isPlaying ? 'negative' : 'primary'"
              :icon="isPlaying ? 'stop' : 'play_arrow'"
              :label="isPlaying ? 'Parar' : 'Tocar'"
              no-caps
              :aria-label="isPlaying ? 'Parar transmissão' : 'Tocar transmissão'"
              @click="togglePlay"
            />
            <audio
              ref="audioEl"
              data-test="radio-audio"
              preload="none"
              style="display: none;"
              @error="onAudioStop"
              @ended="onAudioStop"
            />
          </div>
        </div>

        <template v-if="!affiliateGated">
          <q-separator class="q-my-md" />

          <indique-e-ganhe @gated="affiliateGated = true" />
        </template>
      </div>
    </div>
  </q-page>
</template>

<script>
import axios from "@/axios-auth";
import semPermissao from "@/components/Utils/sem-permissao.vue";
import IndiqueEGanhe from "@/components/Radio/indique-e-ganhe.vue";
import { Dialog, copyToClipboard } from "quasar";

const STATUS_MAP = {
  ok: {
    label: "Rádio disponível",
    color: "positive"
  },
  no_subscription: {
    label: "Sem assinatura",
    color: "grey"
  },
  subscription_inactive: {
    label: "Assinatura cancelada",
    color: "negative"
  },
  no_station_mapped: {
    label: "Configurando rádio",
    color: "warning"
  },
  azuracast_unavailable: {
    label: "Áudio indisponível",
    color: "warning"
  },
  station_not_allowed: {
    label: "Estação indisponível",
    color: "warning"
  }
};

const STATUS_MESSAGES = {
  no_subscription: "Você não tem uma assinatura do Rádio da Sorte ativa.",
  subscription_inactive: "Sua assinatura foi cancelada. Entre em contato com o suporte.",
  no_station_mapped: "Sua rádio está sendo configurada. Aguarde a liberação.",
  azuracast_unavailable: "Sistema de áudio temporariamente indisponível. O link continua disponível para cópia e teste manual.",
  station_not_allowed: "Esta estação não está disponível no seu plano."
};

const POLL_INTERVAL_MS = 25000;
const REGENERATE_CONFIRMATION_MESSAGE = "Isso vai invalidar o link atual. Se a rádio estiver tocando, a máquina pode parar na próxima reconexão. Confirma?";
const PREVIOUS_LISTENER_WARNING = "Detectamos uma máquina tocando agora; ela pode parar na próxima reconexão.";
const STATION_STORAGE_KEY = "radio.selectedShortcode";
const LOWER_PLANS_HINT = "Você pode ouvir a rádio de um plano inferior ao seu. Sua assinatura não muda.";
const STATION_SWITCH_CONFIRMATION_MESSAGE = "Há um ouvinte ao vivo com o link atual. Trocar de estação vai gerar um novo link e invalidar o atual. Confirma?";

export default {
  name: "minha-radio",
  components: {
    semPermissao,
    IndiqueEGanhe
  },
  data() {
    return {
      envelope: null,
      loading: false,
      loadError: "",
      permissionDenied: false,
      regenerating: false,
      regenerateError: "",
      regenerateWarning: "",
      pollTimer: null,
      switchingStation: false,
      affiliateGated: false,
      isPlaying: false
    };
  },
  computed: {
    streamUrl() {
      return this.envelope && this.envelope.listen_url
        ? this.envelope.listen_url
        : "";
    },
    statusInfo() {
      const status = this.envelope && this.envelope.status
        ? this.envelope.status
        : "no_subscription";

      return STATUS_MAP[status] || STATUS_MAP.no_subscription;
    },
    statusMessage() {
      const status = this.envelope && this.envelope.status
        ? this.envelope.status
        : "";

      return STATUS_MESSAGES[status] || "";
    },
    isLive() {
      return !!(this.envelope && Number(this.envelope.listeners_total) > 0);
    },
    nowPlayingText() {
      if (!this.envelope || this.envelope.status === "azuracast_unavailable") {
        return "";
      }

      const nowPlaying = this.envelope.now_playing;
      const song = nowPlaying && nowPlaying.song ? nowPlaying.song : null;

      if (song && song.title && song.artist) {
        return song.title + " - " + song.artist;
      }

      if (song && song.text) {
        return song.text;
      }

      if (this.envelope.status === "ok") {
        return "Tocando agora indisponível";
      }

      return "";
    },
    expiresAtFormatted() {
      if (!this.envelope || !this.envelope.expires_at) {
        return "";
      }

      const expiresAt = new Date(this.envelope.expires_at);
      if (isNaN(expiresAt.getTime())) {
        return "";
      }

      try {
        return expiresAt.toLocaleString("pt-BR", {
          dateStyle: "short",
          timeStyle: "short"
        });
      } catch (error) {
        return expiresAt.toLocaleString("pt-BR");
      }
    },
    canRegenerate() {
      return !!(this.envelope && this.envelope.status === "ok");
    },
    stations() {
      return this.envelope && Array.isArray(this.envelope.stations)
        ? this.envelope.stations
        : [];
    },
    selectedShortcode() {
      return this.envelope ? this.envelope.selected_shortcode : null;
    },
    showStationSelector() {
      return this.stations.length > 1;
    },
    stationOptions() {
      return this.stations.map(station => ({
        label: station.inferior ? station.label + " (plano inferior)" : station.label,
        value: station.shortcode
      }));
    },
    lowerPlansHint() {
      return this.stations.some(station => station.inferior === true)
        ? LOWER_PLANS_HINT
        : "";
    }
  },
  mounted() {
    this.restoreSelectedStation();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.startPolling();
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.releaseAudio();
  },
  watch: {
    selectedShortcode(newShortcode) {
      // Só persiste a seleção que o backend confirmou como tocável; estados
      // sem estação válida (sem assinatura, sem mapeamento, fora do plano)
      // não devem sobrescrever a última escolha boa.
      const status = this.envelope ? this.envelope.status : "";
      if (newShortcode && (status === "ok" || status === "azuracast_unavailable")) {
        this.persistSelectedStation(newShortcode);
      }
    },
    streamUrl(newUrl, oldUrl) {
      if (newUrl === oldUrl) {
        return;
      }
      const audio = this.$refs.audioEl;
      if (!audio) {
        this.isPlaying = false;
        return;
      }
      const wasPlaying = this.isPlaying;
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      if (!wasPlaying || !newUrl) {
        this.isPlaying = false;
        return;
      }
      audio.src = newUrl;
      audio.load();
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          this.isPlaying = false;
        });
      }
    }
  },
  methods: {
    restoreSelectedStation() {
      const persisted = this.readPersistedStation();
      if (!persisted) {
        return this.fetchRadio();
      }

      return this.fetchRadio({ station: persisted });
    },
    persistSelectedStation(shortcode) {
      if (!shortcode || typeof window === "undefined" || !window.localStorage) {
        return;
      }
      try {
        window.localStorage.setItem(STATION_STORAGE_KEY, shortcode);
      } catch (error) {
        // Persistência é best-effort; storage indisponível não quebra a tela.
      }
    },
    readPersistedStation() {
      if (typeof window === "undefined" || !window.localStorage) {
        return "";
      }
      try {
        return window.localStorage.getItem(STATION_STORAGE_KEY) || "";
      } catch (error) {
        return "";
      }
    },
    clearPersistedStation() {
      if (typeof window === "undefined" || !window.localStorage) {
        return;
      }
      try {
        window.localStorage.removeItem(STATION_STORAGE_KEY);
      } catch (error) {
        // best-effort
      }
    },
    fetchRadio(options) {
      const silent = !!(options && options.silent);
      const station = options && options.station ? options.station : "";

      if (!silent) {
        this.loading = true;
        this.loadError = "";
        this.permissionDenied = false;
      }

      const request = station
        ? axios.get("/radio/me", { params: { station: station } })
        : axios.get("/radio/me");

      return request
        .then(res => {
          if (!silent) {
            this.loading = false;
          }

          if (res.error) {
            this.handleLoadError(res.error);
            return;
          }

          const freshEnvelope = res.data ? res.data : res;
          if (freshEnvelope && !freshEnvelope.listen_url && this.envelope && this.envelope.listen_url) {
            freshEnvelope.listen_url = this.envelope.listen_url;
            freshEnvelope.expires_at = this.envelope.expires_at;
          }
          this.envelope = freshEnvelope;

          // Estação pedida deixou de ser permitida (downgrade de plano): limpa a
          // persistência e recai no padrão do plano, em vez de fixar o status inválido.
          if (station && freshEnvelope && freshEnvelope.status === "station_not_allowed") {
            this.clearPersistedStation();
            return this.fetchRadio(silent ? { silent: true } : {});
          }
        })
        .catch(error => {
          if (!silent) {
            this.loading = false;
          }
          this.handleLoadError(error);
        });
    },
    selectStation(shortcode) {
      if (!shortcode || shortcode === this.selectedShortcode) {
        return Promise.resolve();
      }

      if (this.isLive) {
        return new Promise(resolve => {
          Dialog.create({
            title: "Trocar estação",
            message: STATION_SWITCH_CONFIRMATION_MESSAGE,
            ok: { label: "Confirmar troca", color: "negative" },
            cancel: "Cancelar",
            persistent: true
          }).onOk(() => {
            resolve(this._doSelectStation(shortcode));
          }).onCancel(() => {
            resolve();
          });
        });
      }

      return this._doSelectStation(shortcode);
    },

    _doSelectStation(shortcode) {
      this.switchingStation = true;

      return axios.post("/radio/listen", null, { params: { station: shortcode } })
        .then(res => {
          if (res.error) {
            this.handleSwitchError();
            return;
          }

          const env = res.data ? res.data : res;

          if (env && env.status === "station_not_allowed") {
            this.$q.notify({
              message: "Estação indisponível para o seu plano.",
              color: "red",
              position: "top-right"
            });
            return;
          }

          this.envelope = env;
        })
        .catch(() => {
          this.handleSwitchError();
        })
        .then(() => {
          this.switchingStation = false;
        });
    },
    handleSwitchError() {
      this.$q.notify({
        message: "Não foi possível trocar de estação. Tente novamente.",
        color: "red",
        position: "top-right"
      });
    },
    handleLoadError(error) {
      const status = error && error.response ? error.response.status : null;

      this.envelope = null;

      if (status === 401 || status === 403) {
        this.permissionDenied = true;
        return;
      }

      this.loadError = "Não foi possível carregar sua rádio. Verifique a conexão e tente novamente.";
    },
    togglePlay() {
      const audio = this.$refs.audioEl;
      if (!audio) {
        return;
      }
      if (this.isPlaying) {
        this.releaseAudio();
        return;
      }
      if (!this.streamUrl) {
        return;
      }
      audio.src = this.streamUrl;
      audio.load();
      this.isPlaying = true;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          this.isPlaying = false;
        });
      }
    },
    openStreamUrl() {
      if (!this.streamUrl || typeof window === "undefined" || !window.open) {
        return;
      }
      window.open(this.streamUrl, "_blank", "noopener,noreferrer");
    },
    onAudioStop() {
      this.isPlaying = false;
    },
    releaseAudio() {
      const audio = this.$refs.audioEl;
      this.isPlaying = false;
      if (!audio) {
        return;
      }
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    },
    copyStreamUrl() {
      if (!this.streamUrl) {
        return Promise.resolve();
      }

      return copyToClipboard(this.streamUrl)
        .then(() => {
          this.$q.notify({
            message: "Link copiado",
            color: "green",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$q.notify({
            message: "Não foi possível copiar o link",
            color: "red",
            position: "top-right"
          });
        });
    },
    confirmRegenerate() {
      if (this.regenerating || !this.canRegenerate) {
        return;
      }

      if (!this.streamUrl || !this.isLive) {
        return this.regenerateLink();
      }

      let message = REGENERATE_CONFIRMATION_MESSAGE;
      if (this.envelope && this.envelope.previous_listener_active === true) {
        message += "\n\n" + PREVIOUS_LISTENER_WARNING;
      }

      Dialog.create({
        title: "Regenerar link",
        message,
        ok: {
          label: "Regenerar link",
          color: "negative"
        },
        cancel: "Cancelar",
        persistent: true
      }).onOk(() => this.regenerateLink());
    },
    regenerateLink() {
      if (this.regenerating) {
        return Promise.resolve();
      }

      this.regenerating = true;
      this.regenerateError = "";
      this.regenerateWarning = "";

      return axios.post("/radio/regenerate")
        .then(res => {
          if (res.error) {
            this.handleRegenerateError();
            return;
          }

          const envelope = res.data ? res.data : res;
          this.envelope = envelope;

          if (envelope.previous_listener_active === true) {
            this.regenerateWarning = PREVIOUS_LISTENER_WARNING;
          }

          this.$q.notify({
            message: "Novo link gerado",
            color: "green",
            position: "top-right"
          });
        })
        .catch(() => {
          this.handleRegenerateError();
        })
        .then(() => {
          this.regenerating = false;
        });
    },
    handleRegenerateError() {
      this.regenerateError = "Não foi possível regenerar o link. Tente novamente.";
      this.$q.notify({
        message: "Não foi possível regenerar o link",
        color: "red",
        position: "top-right"
      });
    },
    startPolling() {
      if (this.pollTimer || document.visibilityState !== "visible") {
        return;
      }

      this.pollTimer = window.setInterval(() => {
        if (document.visibilityState === "visible" && !this.regenerating && !this.switchingStation) {
          this.fetchRadio({ silent: true, station: this.selectedShortcode });
        }
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (!this.pollTimer) {
        return;
      }

      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    onVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.fetchRadio({ silent: true, station: this.selectedShortcode });
        this.startPolling();
        return;
      }

      this.stopPolling();
    }
  }
};
</script>

<style scoped>
.radio-page__content {
  max-width: 960px;
}

.radio-page__header,
.radio-page__title-line,
.radio-page__actions,
.radio-page__playback {
  display: flex;
  align-items: center;
  gap: 8px;
}

.radio-page__header,
.radio-page__playback {
  justify-content: space-between;
}

.radio-page__title-line,
.radio-page__actions {
  flex-wrap: wrap;
}

.radio-page__activity,
.radio-page__label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
}

.radio-page__banner {
  font-size: 14px;
  line-height: 1.5;
}

.radio-page__url {
  width: 100%;
}

.radio-page__station {
  max-width: 360px;
}

.radio-page__audio {
  width: 100%;
}

.radio-page__audio audio {
  width: 100%;
}

.radio-page__now-playing {
  min-width: 220px;
}

@media (max-width: 600px) {
  .radio-page__header,
  .radio-page__playback {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
